// Board-admin view of the tenant's audit log. Shows the hash-chained
// event stream + runs link-integrity verification client-side so the
// "Chain valid ✓" badge isn't trusted from the server.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface AuditEntry {
  id: string;
  sequence: number;
  event_type: string;
  election_id: string | null;
  actor_user_id: string | null;
  actor_resident_id: string | null;
  prev_hash: string;
  hash: string;
  payload: Record<string, unknown>;
  created_at: number;
}

interface Resp {
  entries: AuditEntry[];
  head: { sequence: number; hash: string } | null;
  nextBeforeSequence: number | null;
}

const GENESIS = '0'.repeat(64);

export function AuditLog() {
  const list = useQuery<Resp>({
    queryKey: ['audit', 'page1'],
    queryFn: () => api.get('/audit?limit=500'),
  });

  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const verification = useMemo(() => {
    if (!list.data?.entries) return null;
    // Chain came back DESC by sequence. Verify in ASCENDING order.
    const asc = [...list.data.entries].reverse();
    let prev = GENESIS;
    let prevSeq = asc[0] ? asc[0].sequence - 1 : 0;
    for (const r of asc) {
      if (r.sequence !== prevSeq + 1 && asc.length === list.data.head!.sequence) {
        return { ok: false, brokenAt: r.sequence, reason: `Sequence gap (expected ${prevSeq + 1})` };
      }
      // Only check link continuity within what we received (we may not have row 1).
      if (r.prev_hash !== prev && asc[0]!.sequence === 1) {
        return { ok: false, brokenAt: r.sequence, reason: `prev_hash mismatch` };
      }
      // Always check that prev row's hash matches THIS row's prev_hash within the loaded set.
      if (asc.indexOf(r) > 0 && r.prev_hash !== prev) {
        return { ok: false, brokenAt: r.sequence, reason: `prev_hash does not link to previous row` };
      }
      prev = r.hash;
      prevSeq = r.sequence;
    }
    return { ok: true };
  }, [list.data]);

  const filtered = useMemo(() => {
    if (!list.data) return [];
    if (!filter) return list.data.entries;
    const f = filter.toLowerCase();
    return list.data.entries.filter((e) => e.event_type.includes(f) || e.hash.startsWith(f));
  }, [list.data, filter]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-ink-900">Audit log</h1>
          <p className="mt-1.5 text-ink-600">
            Append-only hash chain of every action in your HOA. Tamper-evident by design.
          </p>
        </div>
        {verification && (
          <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
            verification.ok ? 'bg-brand-50 text-brand-700' : 'bg-red-50 text-red-700'
          }`}>
            {verification.ok ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                Chain integrity verified
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                Broken at #{verification.brokenAt}: {verification.reason}
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 mb-4">
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by event type or hash prefix…"
          className="flex-1 max-w-md border border-ink-200 rounded-lg px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition outline-none"
        />
        {list.data?.head && (
          <div className="text-xs text-ink-500">
            Head: <code className="font-mono text-ink-700">#{list.data.head.sequence}</code> ·{' '}
            <code className="font-mono">{list.data.head.hash.slice(0, 8)}…</code>
          </div>
        )}
      </div>

      <div className="bg-white border border-ink-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 border-b border-ink-200">
            <tr className="text-left">
              <th className="px-4 py-2.5 font-semibold text-ink-700 w-16">#</th>
              <th className="px-4 py-2.5 font-semibold text-ink-700">Event</th>
              <th className="px-4 py-2.5 font-semibold text-ink-700">When</th>
              <th className="px-4 py-2.5 font-semibold text-ink-700 w-32">Hash</th>
              <th className="px-4 py-2.5 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-ink-500">Loading audit chain…</td></tr>
            )}
            {filtered.map((e) => {
              const isOpen = expanded.has(e.id);
              return (
                <Row
                  key={e.id}
                  entry={e}
                  isOpen={isOpen}
                  onToggle={() => {
                    const n = new Set(expanded);
                    if (isOpen) n.delete(e.id); else n.add(e.id);
                    setExpanded(n);
                  }}
                />
              );
            })}
            {!list.isLoading && filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-ink-500">No matching entries.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-ink-500">
        Showing {filtered.length} of {list.data?.entries.length ?? 0} entries.
        {' '}Verification runs in your browser — it confirms each row's <code>prev_hash</code> matches the previous row's <code>hash</code>.
      </p>
    </div>
  );
}

function Row({ entry, isOpen, onToggle }: { entry: AuditEntry; isOpen: boolean; onToggle: () => void }) {
  return (
    <>
      <tr className="border-b border-ink-100 last:border-b-0 hover:bg-ink-50/50 transition cursor-pointer" onClick={onToggle}>
        <td className="px-4 py-2.5 text-ink-600 font-mono text-xs">{entry.sequence}</td>
        <td className="px-4 py-2.5">
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${eventTypeColor(entry.event_type)}`}>
            {entry.event_type}
          </span>
        </td>
        <td className="px-4 py-2.5 text-ink-600 text-xs">{new Date(entry.created_at).toLocaleString()}</td>
        <td className="px-4 py-2.5">
          <code className="font-mono text-xs text-ink-500">{entry.hash.slice(0, 12)}…</code>
        </td>
        <td className="px-4 py-2.5 text-ink-400">{isOpen ? '−' : '+'}</td>
      </tr>
      {isOpen && (
        <tr className="bg-ink-50/40 border-b border-ink-100">
          <td colSpan={5} className="px-4 py-4">
            <dl className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-x-6 gap-y-1.5 text-xs">
              <dt className="text-ink-500">Sequence</dt><dd className="font-mono text-ink-900">{entry.sequence}</dd>
              <dt className="text-ink-500">Event</dt><dd className="font-mono text-ink-900">{entry.event_type}</dd>
              <dt className="text-ink-500">Prev hash</dt><dd className="font-mono text-ink-700 break-all">{entry.prev_hash}</dd>
              <dt className="text-ink-500">Hash</dt><dd className="font-mono text-ink-700 break-all">{entry.hash}</dd>
              {entry.election_id && (<><dt className="text-ink-500">Election</dt><dd className="font-mono text-ink-700">{entry.election_id}</dd></>)}
              {entry.actor_user_id && (<><dt className="text-ink-500">Actor (user)</dt><dd className="font-mono text-ink-700">{entry.actor_user_id}</dd></>)}
              {entry.actor_resident_id && (<><dt className="text-ink-500">Actor (resident)</dt><dd className="font-mono text-ink-700">{entry.actor_resident_id}</dd></>)}
              <dt className="text-ink-500">Payload</dt>
              <dd>
                <pre className="font-mono bg-white border border-ink-200 rounded p-2 overflow-x-auto text-ink-800">
{JSON.stringify(entry.payload, null, 2)}
                </pre>
              </dd>
            </dl>
          </td>
        </tr>
      )}
    </>
  );
}

function eventTypeColor(t: string): string {
  if (t.startsWith('ballot.')) return 'bg-brand-50 text-brand-700';
  if (t.startsWith('election.')) return 'bg-amber-50 text-amber-700';
  if (t.startsWith('user.') || t === 'tenant.created') return 'bg-blue-50 text-blue-700';
  if (t.startsWith('resident.') || t.startsWith('lot.')) return 'bg-violet-50 text-violet-700';
  if (t.startsWith('email_provider.')) return 'bg-pink-50 text-pink-700';
  return 'bg-ink-100 text-ink-700';
}
